import React, { useEffect, useState } from "react";
import { Box, Card, CardContent, Grid, Divider, Button } from "@material-ui/core";
import "moment-timezone";
import TabHeader from "src/components/TabHeader/TabHeader";
import { YardRoute } from "../../yard.routes";
import { useStyles } from "./components/style";
import { YardContainer } from "./components/YardContainer";
import CommonAutoCompleteField from "src/components/CommonAutoCompleteField/CommonAutoCompleteField";
import { useAppDispatch, useAppSelector } from "src/app-redux/hooks";
import { Backdrop } from "@mui/material";
import {
  getActiveAllContainerYardsListingAction,
  getContainerYardRowsSlotsAction,
} from "src/app-redux/yard/actions/yardActions";
import {
  loader,
  addYardId,
  selectAllYardsListing,
  flushComponentRecord,
  selectComponentYardId,
  addComponentYardId,
  addVehicleId,
  setYardRowSlots,
  selectYardRowsSlots,
} from "src/app-redux/yard/yardSlice";
import CachedIcon from "@material-ui/icons/Cached";
import { fetchWrapper } from "src/api-client/fetchWrapper";
import Drawer from "./components/Drawer";
import { getVehicleDetailsByIdAction } from "src/app-redux/vehicle/actions/vehicleActions";
import { setSelectedVehicleId } from "src/app-redux/vehicle/vehicleSlice";
import moment from "moment";
import { mkConfig, generateCsv, download } from "export-to-csv";
import DownloadIcon from "@mui/icons-material/Download";
import { addNotificationAction } from "src/app-redux/settings/settingsSlice";
import { minutesToDhms } from "./components/VehicleImages";
import CommonLoader from "src/components/Loader/CommonLoader";

interface Props {}

export function Listing({}: Props): React.ReactElement {
  const dispatch = useAppDispatch();
  const isExportString = localStorage.getItem("isAllowToExportData");
  const isExport = isExportString === "true";
  const classes = useStyles();
  const moduleName = YardRoute.title;
  const pageHeading = "Yard";
  const componentYardId = useAppSelector(selectComponentYardId);
  const loading = useAppSelector(loader);
  const allYards: any = useAppSelector(selectAllYardsListing);
  const [vehicleRegNumberArr, setvehicleRegNumberArr] = React.useState<any>({});
  const [selectedVehicle, setSelectedVehicle] = React.useState<string>("");
  const [isDrawerOpen, setisDrawerOpen] = React.useState(false);
  const rowsRecord = useAppSelector(selectYardRowsSlots);
  const [yardName, setYardName] = useState("");

  const refreshPage = () => {
    dispatch(getContainerYardRowsSlotsAction(componentYardId));
  };

  const handleYardChange = (id: number) => {
    dispatch(addComponentYardId(id));
    dispatch(addYardId(id));
    dispatch(getContainerYardRowsSlotsAction(id));
  };

  const handleCloseDrawer = () => {
    dispatch(setSelectedVehicleId(""));
    dispatch(addVehicleId(""));
    setisDrawerOpen(false);
  };

  const csvConfig = mkConfig({
    fieldSeparator: ",",
    decimalSeparator: ".",
    useKeysAsHeaders: true,
    filename: "Yard",
  });

  const filterExportData = () => {
    const yard = yardName
      ? allYards.find((st: any) => st.id === yardName)?.name
      : allYards[0]?.name;

    const result = rowsRecord
      .filter((data: any) => data.noOfVehicles > 0)
      .flatMap((row: any) =>
        row.slots
          .filter((rec: any) => rec.vehicle)
          .map((slot: any) => ({
            Yard: yard,
            Row: `Row ${row.rowName}`,
            Slot: slot.slotOrder,
            "Vehicle No": slot.vehicle.vehicleRegistration,
            "Entry date": moment(new Date(slot.vehicle.yardDateTime)).format("DD/MM/YYYY hh:mm a"),
            "Vehicle Status": slot.vehicle?.vehicleStatus,
            "Consignment Type": slot.vehicle?.consignmentType,
            Cargo: slot.vehicle.cargoType,
            "Yard stay time": minutesToDhms(slot?.vehicle?.vehicleInYardMinutes),
          }))
      );
    return result;
  };

  const handleExportData = async () => {
    const result = await filterExportData();
    if (result) {
      dispatch(
        addNotificationAction({
          message: "Your download will begin shortly",
          options: { variant: "success" },
        })
      );
      const csv = generateCsv(csvConfig)(result);
      download(csvConfig)(csv);
    }
  };

  const getVehicleContainerNumbers = async () => {
    const results = await fetchWrapper.get("get-all-vehicles-and-containers-for-gate-out");
    if (results.isSuccess) {
      setvehicleRegNumberArr(results.value);
    }
    return;
  };

  useEffect(() => {
    if (allYards.length !== 0) {
      dispatch(getContainerYardRowsSlotsAction(allYards[0].id));
      dispatch(addYardId(allYards[0].id));
      dispatch(addComponentYardId(allYards[0].id));
    } else {
      dispatch(setYardRowSlots([]));
    }
  }, [allYards]);

  useEffect(() => {
    filterExportData();
  }, [rowsRecord]);

  useEffect(() => {
    refreshPage();
    dispatch(getActiveAllContainerYardsListingAction());
    getVehicleContainerNumbers();
    return () => {
      dispatch(flushComponentRecord());
    };
  }, []);

  return (
    <>
      <Drawer
        isDrawerOpen={isDrawerOpen}
        handleCloseDrawer={() => {
          handleCloseDrawer();
        }}
      />
      <Backdrop className={classes.backdrop} open={loading}>
        <CommonLoader />
      </Backdrop>

      <TabHeader module={moduleName} heading={pageHeading} />
      <Box m={2} />
      <Card>
        <CardContent className={classes.cardMain} style={{ height: "auto" }}>
          <Grid container direction="row" justifyContent="space-between">
            <Grid xs={11} md={3}>
              <Box mt={1} mb={2}>
                <Grid item md={12}>
                  <Box>
                    <CommonAutoCompleteField
                      cssClassName="customTextField"
                      label="Yard Name"
                      placeholder="Select Yard Name"
                      name="yardId"
                      disableClearable={true}
                      optionsType={allYards}
                      getOptionLabel={(option) => option.name}
                      required
                      value={allYards.find((st: any) => st.id === componentYardId) || null}
                      onChange={(option: any) => {
                        if (option && option.name !== "" && option.id !== 0) {
                          setYardName(option.id);
                          handleYardChange(option.id);
                          setSelectedVehicle("");
                        }
                      }}
                    />
                  </Box>
                </Grid>
              </Box>
            </Grid>

            <Grid xs={1} md={6}>
              <span className={classes.refreshIcon}>
                <CachedIcon
                  onClick={() => {
                    refreshPage();
                  }}
                />

                <Button
                  style={{ marginLeft: "10px" }}
                  // variant="contained"
                  disabled={isExport && filterExportData().length == 0}
                  onClick={() => handleExportData()}
                  startIcon={<DownloadIcon />}
                >
                  Export data to Excel
                </Button>
              </span>
            </Grid>

            <Grid xs={12} md={3}>
              <Box mt={1} mb={2}>
                <Grid item md={12}>
                  <Box>
                    <CommonAutoCompleteField
                      cssClassName="customTextField"
                      label="Container Number"
                      placeholder="Enter Container Number"
                      name="containerNumber"
                      optionsType={vehicleRegNumberArr?.containerList}
                      getOptionLabel={(option) => option?.containerNumber}
                      value={
                        vehicleRegNumberArr?.containerList?.find(
                          (st: any) => st?.gateEntryId == selectedVehicle
                        ) || null
                      }
                      disableClearable={false}
                      onChange={async (option: any) => {
                        setSelectedVehicle(option?.gateEntryId);
                        dispatch(getVehicleDetailsByIdAction(option?.gateEntryId));
                        dispatch(setSelectedVehicleId(option?.gateEntryId));
                        setisDrawerOpen(true);
                      }}
                    />
                  </Box>
                </Grid>
              </Box>
            </Grid>
          </Grid>
          <Divider />
          <YardContainer yardId={componentYardId} />
        </CardContent>
      </Card>
    </>
  );
}
